import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  Shield,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Clock,
  Award,
  BarChart3,
  Target,
  Zap,
  FileText,
  Phone,
  Mail,
  Search,
  RefreshCw,
  Users
} from 'lucide-react';

const DenialManagementClinics = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Denial Management for Medical Clinics | MDeRCM</title>
        <meta 
          name="description" 
          content="Expert denial management for medical clinics. Recover 65-75% of denied claims, reduce denial rates 35-45%. AI-powered prevention & appeals." 
        />
        <meta 
          name="keywords" 
          content="denial management services for medical clinics, claim denial recovery, medical claim appeals, denial prevention, reduce claim denials, denial management solutions, healthcare denial services" 
        />
        <link rel="canonical" href="https://mdercm.com/denial-management-services-medical-clinics" />
      </Helmet>

      <main style={{ minHeight: '100vh', backgroundColor: '#ffffff', fontFamily: "'Poppins', sans-serif" }}>
        
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
          color: '#ffffff',
          padding: isMobile ? '60px 20px' : '80px 40px'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ maxWidth: '56rem', margin: '0 auto', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '8px 20px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                🛡️ Specialized Denial Recovery Experts
              </div>
              
              <h1 style={{
                fontSize: isMobile ? '2rem' : '3rem',
                fontWeight: 'bold',
                marginBottom: '24px',
                lineHeight: '1.2'
              }}>
                Denial Management Services for Medical Clinics
              </h1>
              
              <p style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                marginBottom: '32px',
                opacity: 0.95,
                lineHeight: '1.75'
              }}>
                Stop accepting denied claims as lost revenue. Our specialized denial management services recover 65-75% of denied claims, reduce future denial rates by 40%, and put $50,000-$200,000 back into your practice annually—guaranteed results within 60 days.
              </p>

              {/* Key Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                gap: '20px',
                marginTop: '40px'
              }}>
                {[
                  { icon: DollarSign, value: '65-75%', label: 'Claims Recovered' },
                  { icon: TrendingUp, value: '40%', label: 'Denial Reduction' },
                  { icon: Clock, value: '24-48hrs', label: 'Appeal Speed' },
                  { icon: Award, value: '80%+', label: 'Appeal Success' }
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <stat.icon size={32} style={{ marginBottom: '12px' }} />
                    <div style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '4px' }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '16px',
                justifyContent: 'center',
                marginTop: '40px'
              }}>
                <Link to="/free-trial" style={{
                  textDecoration: 'none',
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
                >
                  Get Free Denial Audit →
                </Link>
                
                <a href="tel:+15103566069" style={{
                  textDecoration: 'none',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  📞 Call: +1 (510) 356-6069
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              Claim denials are bleeding your clinic dry. Industry data shows that the average medical clinic experiences 10-15% denial rates, with <strong style={{ color: '#9333ea' }}>63% of denied claims never being reworked</strong>. At an average reimbursement of $150 per claim, a clinic processing 500 claims monthly is losing $45,000-$67,500 annually to unworked denials alone.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              <strong style={{ color: '#9333ea' }}>Denial management services for medical clinics</strong> aren't optional—they're essential for financial survival. Every denied claim represents revenue you've already earned through patient care. Without dedicated denial management, you're essentially working for free on 10-15% of your encounters.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151'
            }}>
              Our specialized denial management service doesn't just recover lost revenue—we systematically eliminate the root causes of denials, dramatically reducing future denial rates while maximizing recovery on existing denials. The result? An immediate influx of recovered revenue plus sustained improvements that compound over time.
            </p>
          </div>
        </section>

        {/* The Denial Crisis */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#faf5ff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              The Hidden Cost of Denial Mismanagement
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#9333ea',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto',
              fontWeight: '600'
            }}>
              Most clinics vastly underestimate their denial problem
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  title: 'The Obvious Losses',
                  items: [
                    { label: 'Direct revenue loss from denials', value: '$45K-$135K annually' },
                    { label: 'Staff time working denials', value: '15-25 hrs/week' },
                    { label: 'Appeal costs and expenses', value: '$8K-$15K annually' }
                  ],
                  color: '#dc2626'
                },
                {
                  title: 'The Hidden Costs',
                  items: [
                    { label: 'Cash flow disruption impact', value: '$30K-$80K tied up' },
                    { label: 'Staff frustration & turnover', value: '$15K-$30K per person' },
                    { label: 'Missed prevention opportunities', value: '$20K-$50K annually' }
                  ],
                  color: '#ea580c'
                },
                {
                  title: 'Compounding Problems',
                  items: [
                    { label: 'Delayed payments hurt operations', value: '45-60 day delays' },
                    { label: 'Pattern denials go unaddressed', value: 'Recurring losses' },
                    { label: 'Payer relationship damage', value: 'Future claim scrutiny' }
                  ],
                  color: '#f59e0b'
                },
                {
                  title: 'Opportunity Costs',
                  items: [
                    { label: 'Time not spent on patient care', value: '10-15 patients/week' },
                    { label: 'Practice growth initiatives delayed', value: 'Lost expansion' },
                    { label: 'Staff demoralization effects', value: 'Reduced productivity' }
                  ],
                  color: '#d97706'
                }
              ].map((section, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: `3px solid ${section.color}`,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: section.color,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <AlertTriangle size={24} />
                    {section.title}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {section.items.map((item, itemIdx) => (
                      <div key={itemIdx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '0.9375rem', color: '#374151', flex: 1 }}>
                          {item.label}
                        </span>
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: section.color,
                          textAlign: 'right',
                          whiteSpace: 'nowrap'
                        }}>
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '32px',
              backgroundColor: '#dc2626',
              color: '#ffffff',
              padding: '32px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.125rem', marginBottom: '12px' }}>
                💰 Total Annual Impact for Average Clinic
              </div>
              <div style={{ fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 'bold', marginBottom: '8px' }}>
                $150,000 - $400,000
              </div>
              <div style={{ fontSize: '1rem', opacity: 0.9 }}>
                In lost revenue, wasted time, and missed opportunities
              </div>
            </div>
          </div>
        </section>

        {/* Our 4-Step Process */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Our Proven 4-Step Denial Management Process
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              Systematic approach that recovers revenue and prevents future denials
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '32px'
            }}>
              {[
                {
                  step: 'STEP 1',
                  title: 'Rapid Denial Discovery & Analysis',
                  icon: Search,
                  duration: 'Days 1-7',
                  color: '#9333ea',
                  activities: [
                    'Comprehensive audit of all open denials (12-24 months)',
                    'Identify high-value recoverable claims',
                    'Categorize denials by reason code and payer',
                    'Analyze denial patterns and trends',
                    'Calculate total recoverable revenue',
                    'Prioritize claims by value and appeal deadline'
                  ],
                  outcome: 'Clear roadmap of $50K-$200K recovery opportunity'
                },
                {
                  step: 'STEP 2',
                  title: 'Aggressive Claim Recovery',
                  icon: RefreshCw,
                  duration: 'Days 8-45',
                  color: '#7c3aed',
                  activities: [
                    'Fast-track appeals on highest-value claims',
                    'Gather supporting documentation systematically',
                    'Submit compelling, payer-specific appeals',
                    'Follow up persistently within appeal timeframes',
                    'Escalate to secondary and tertiary levels',
                    'Negotiate settlements on complex denials'
                  ],
                  outcome: '65-75% of denied claims recovered within 30-45 days'
                },
                {
                  step: 'STEP 3',
                  title: 'Root Cause Elimination',
                  icon: Target,
                  duration: 'Days 30-90',
                  color: '#6366f1',
                  activities: [
                    'Deep-dive analysis of denial root causes',
                    'Implement prevention strategies by denial type',
                    'Train staff on common denial triggers',
                    'Fix process gaps and system issues',
                    'Establish proactive verification protocols',
                    'Create denial prevention workflows'
                  ],
                  outcome: 'Denial rate drops 30-40% within 60-90 days'
                },
                {
                  step: 'STEP 4',
                  title: 'Ongoing Monitoring & Optimization',
                  icon: BarChart3,
                  duration: 'Day 91+',
                  color: '#818cf8',
                  activities: [
                    'Real-time denial trend monitoring',
                    'Weekly denial management team meetings',
                    'Monthly performance reporting and review',
                    'Continuous process refinement',
                    'Payer-specific strategy adjustments',
                    'Staff training updates and refreshers'
                  ],
                  outcome: 'Sustained 4-6% denial rate maintained long-term'
                }
              ].map((step, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#faf5ff',
                  borderRadius: '16px',
                  padding: '32px 24px',
                  border: `3px solid ${step.color}`,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: step.color,
                    color: '#ffffff',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    letterSpacing: '0.5px'
                  }}>
                    {step.step}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '8px',
                    marginBottom: '16px'
                  }}>
                    <step.icon size={48} style={{ color: step.color }} />
                  </div>

                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    marginBottom: '8px',
                    color: '#111827'
                  }}>
                    {step.title}
                  </h3>

                  <div style={{
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    marginBottom: '24px',
                    fontWeight: '600'
                  }}>
                    ⏱️ {step.duration}
                  </div>

                  <div style={{
                    backgroundColor: '#ffffff',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: step.color,
                      marginBottom: '12px'
                    }}>
                      KEY ACTIVITIES:
                    </div>
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0
                    }}>
                      {step.activities.map((activity, actIdx) => (
                        <li key={actIdx} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          marginBottom: '10px',
                          fontSize: '0.875rem',
                          color: '#4b5563'
                        }}>
                          <CheckCircle size={16} style={{ color: step.color, flexShrink: 0, marginTop: '2px' }} />
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={{
                    backgroundColor: step.color,
                    color: '#ffffff',
                    padding: '16px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      marginBottom: '6px',
                      opacity: 0.9,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      EXPECTED OUTCOME:
                    </div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: '600' }}>
                      {step.outcome}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Tiers */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#faf5ff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Choose Your Service Level
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  name: 'Recovery Only',
                  price: 'Performance-Based',
                  description: 'Focus on recovering existing denied claims',
                  features: [
                    'Full denial audit & analysis',
                    'Aggressive appeal management',
                    'All levels of appeals handled',
                    'No upfront costs',
                    'Pay only on recovered claims',
                    '25-30% of recovered revenue'
                  ],
                  best: 'Backlog of 100+ denied claims',
                  color: '#9333ea'
                },
                {
                  name: 'Prevention + Recovery',
                  price: '5-7% of Collections',
                  description: 'Comprehensive denial management solution',
                  features: [
                    'Everything in Recovery Only',
                    'Proactive denial prevention',
                    'Real-time claim scrubbing',
                    'Staff training & education',
                    'Monthly trend reporting',
                    'Process improvement consulting'
                  ],
                  best: 'Ongoing high denial rates (8-15%)',
                  color: '#7c3aed',
                  popular: true
                },
                {
                  name: 'Full RCM + Denial Mgmt',
                  price: '4-6% of Collections',
                  description: 'Complete revenue cycle management',
                  features: [
                    'Everything in Prevention+',
                    'Complete billing operations',
                    'Coding & charge capture',
                    'AR management & collections',
                    'Patient billing & support',
                    'Comprehensive reporting'
                  ],
                  best: 'Complete billing outsourcing',
                  color: '#6366f1'
                }
              ].map((tier, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  padding: '32px 24px',
                  border: tier.popular ? `3px solid ${tier.color}` : '2px solid #e5e7eb',
                  boxShadow: tier.popular ? '0 10px 30px rgba(124, 58, 237, 0.2)' : '0 4px 6px rgba(0,0,0,0.1)',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(124, 58, 237, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = tier.popular ? '0 10px 30px rgba(124, 58, 237, 0.2)' : '0 4px 6px rgba(0,0,0,0.1)';
                }}
                >
                  {tier.popular && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: '#F26522',
                      color: '#ffffff',
                      padding: '4px 16px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '700'
                    }}>
                      MOST POPULAR
                    </div>
                  )}

                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    marginBottom: '8px',
                    color: tier.color,
                    textAlign: 'center'
                  }}>
                    {tier.name}
                  </h3>

                  <div style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#111827',
                    textAlign: 'center',
                    marginBottom: '8px'
                  }}>
                    {tier.price}
                  </div>

                  <p style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textAlign: 'center',
                    marginBottom: '24px',
                    minHeight: '40px'
                  }}>
                    {tier.description}
                  </p>

                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 24px 0'
                  }}>
                    {tier.features.map((feature, featIdx) => (
                      <li key={featIdx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '12px',
                        fontSize: '0.9375rem',
                        color: '#374151'
                      }}>
                        <CheckCircle size={18} style={{ color: tier.color, flexShrink: 0, marginTop: '2px' }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div style={{
                    backgroundColor: `${tier.color}15`,
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    borderLeft: `4px solid ${tier.color}`
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: tier.color,
                      fontWeight: '600',
                      marginBottom: '4px'
                    }}>
                      BEST FOR:
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                      {tier.best}
                    </div>
                  </div>

                  <Link to="/free-trial" style={{
                    display: 'block',
                    textAlign: 'center',
                    backgroundColor: tier.color,
                    color: '#ffffff',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.9375rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = tier.color === '#9333ea' ? '#7c3aed' : tier.color === '#7c3aed' ? '#6366f1' : '#818cf8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = tier.color;
                  }}
                  >
                    Get Started →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Results Tracking */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              How We Track Your Success
            </h2>

            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '48px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                gap: '2px',
                backgroundColor: '#e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '24px'
              }}>
                <div style={{
                  backgroundColor: '#ffffff',
                  padding: '16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem'
                }}>
                  Key Metric
                </div>
                <div style={{
                  backgroundColor: '#fee2e2',
                  padding: '16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#dc2626',
                  fontSize: '0.875rem'
                }}>
                  Baseline
                </div>
                <div style={{
                  backgroundColor: '#fef3c7',
                  padding: '16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#d97706',
                  fontSize: '0.875rem'
                }}>
                  60 Days
                </div>
                <div style={{
                  backgroundColor: '#dcfce7',
                  padding: '16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#16a34a',
                  fontSize: '0.875rem'
                }}>
                  120 Days
                </div>
              </div>

              {[
                { metric: 'Initial Denial Rate', baseline: '12-15%', day60: '8-10%', day120: '4-6%' },
                { metric: 'Denials Worked', baseline: '30-40%', day60: '90-95%', day120: '100%' },
                { metric: 'Appeal Success Rate', baseline: '35-45%', day60: '60-70%', day120: '75-85%' },
                { metric: 'Time to Work Denial', baseline: '30-60 days', day60: '7-14 days', day120: '24-48 hrs' },
                { metric: 'Revenue Recovered', baseline: '$8K/mo', day60: '$25K/mo', day120: '$35K/mo' },
                { metric: 'Net Revenue Impact', baseline: 'Baseline', day60: '+15-20%', day120: '+25-30%' }
              ].map((row, idx) => (
                <div key={idx} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                  gap: '2px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    backgroundColor: '#ffffff',
                    padding: '16px',
                    color: '#111827',
                    fontWeight: '500',
                    fontSize: '0.9375rem'
                  }}>
                    {row.metric}
                  </div>
                  <div style={{
                    backgroundColor: '#fee2e2',
                    padding: '16px',
                    textAlign: 'center',
                    color: '#dc2626',
                    fontWeight: '600',
                    fontSize: '0.9375rem'
                  }}>
                    {row.baseline}
                  </div>
                  <div style={{
                    backgroundColor: '#fef3c7',
                    padding: '16px',
                    textAlign: 'center',
                    color: '#d97706',
                    fontWeight: '600',
                    fontSize: '0.9375rem'
                  }}>
                    {row.day60}
                  </div>
                  <div style={{
                    backgroundColor: '#dcfce7',
                    padding: '16px',
                    textAlign: 'center',
                    color: '#16a34a',
                    fontWeight: '600',
                    fontSize: '0.9375rem'
                  }}>
                    {row.day120}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#faf5ff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Common Questions About Denial Management
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  q: 'How quickly will I see recovered revenue?',
                  a: 'Most clinics see first recovered payments within 14-21 days of starting our service. High-value, straightforward denials are appealed first for fastest return. By day 30, clients typically recover $15,000-$40,000. Full recovery potential is usually realized within 60-90 days.'
                },
                {
                  q: 'What if my denials are too old to appeal?',
                  a: 'While timely filing limits vary by payer (typically 90-365 days), we often recover "expired" denials through: corrected claims, reconsideration requests, provider relations negotiations, and identifying claims that weren\'t actually denied but were mishandled. Even old denials have 40-60% recovery potential.'
                },
                {
                  q: 'Do you handle all denial types?',
                  a: 'Yes. We manage all denial categories: coding/billing errors, missing information, medical necessity, authorization issues, timely filing, duplicate claims, coordination of benefits, credentialing, and more. We also handle both initial denials and appeal denials through all levels (first, second, third-party review).'
                },
                {
                  q: 'How is this different from what my staff does?',
                  a: 'Your staff likely works denials reactively when time allows. We provide: dedicated denial specialists (not divided attention), systematic processes (not ad-hoc), payer-specific expertise (not general knowledge), aggressive follow-up (not sporadic), root cause analysis (not just recovery), and advanced technology (not manual tracking).'
                },
                {
                  q: 'What if we have low denial rates already?',
                  a: 'Even "low" 5-7% rates benefit from professional management. We focus on: recovering the denials you do have, improving appeal success rates (most clinics: 40%, ours: 75-85%), reducing time to work denials, ensuring zero denials go unworked, and implementing prevention strategies to push rates to 3-4%.'
                },
                {
                  q: 'Can you guarantee results?',
                  a: 'We guarantee measurable improvement within 60 days or we work for free until you see results. Typical outcomes: 65-75% recovery rate on denied claims, 30-40% reduction in ongoing denial rates, and ROI of 300-500% in year one. If we don\'t deliver, you don\'t pay.'
                }
              ].map((faq, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#9333ea',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <Shield size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{faq.q}</span>
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.75',
                    color: '#374151',
                    margin: 0,
                    paddingLeft: '28px'
                  }}>
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
          color: '#ffffff'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Stop Losing Revenue to Denied Claims
            </h2>
            <p style={{
              fontSize: '1.125rem',
              marginBottom: '32px',
              opacity: 0.95
            }}>
              Get a free denial audit and see exactly how much revenue we can recover for your clinic
            </p>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '16px',
              justifyContent: 'center',
              marginBottom: '32px'
            }}>
              <Link to="/free-trial" style={{
                textDecoration: 'none',
                backgroundColor: '#F26522',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block'
              }}>
                Get Free Denial Audit →
              </Link>
              
              <a href="tel:+15103566069" style={{
                textDecoration: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                📞 +1 (510) 356-6069
              </a>
            </div>

            <p style={{
              fontSize: '0.9375rem',
              opacity: 0.9
            }}>
              💰 Typical recovery: $50,000-$200,000 • 65-75% success rate • Results in 60 days
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              textAlign: 'center',
              marginBottom: '32px',
              color: '#111827'
            }}>
              Related Solutions
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px'
            }}>
              {[
                { title: 'Reduce Claim Denials', link: '/reduce-claim-denials-healthcare-billing' },
                { title: 'Small Practice Billing', link: '/medical-billing-outsourcing-small-practices' },
                { title: 'View Pricing Options', link: '/pricing' }
              ].map((link, idx) => (
                <Link
                  key={idx}
                  to={link.link}
                  style={{
                    padding: '20px',
                    backgroundColor: '#faf5ff',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '2px solid #e9d5ff',
                    transition: 'all 0.3s ease',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#9333ea';
                    e.currentTarget.style.backgroundColor = '#f5f3ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e9d5ff';
                    e.currentTarget.style.backgroundColor = '#faf5ff';
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#9333ea'
                  }}>
                    {link.title} →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default DenialManagementClinics;